import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import MultiSelect from '../../inputs/MultiSelect'
import { topicLabelFor } from '../../../utils/topicLabel'
import { ATTR_OPTIONS } from './commentFilter'

// Non-modal filter popover for the comments pane, anchored to the toolbar
// filter button. Mirrors the Library nav popover (CodeFilterPopover): an
// Any/All operator combines the facets, each facet is a MultiSelect (attrs are
// toggle chips), and there's a descendants roll-up checkbox + "Clear all".
// Closes on Escape, outside click, or re-clicking the anchor.
function CommentsFilterPopover({
  anchorEl,
  filter,
  onPatch,
  onClear,
  onClose,
  active,
  authorOptions,
  itemOptions,
  topicOptions,
}) {
  const ref = useRef(null)
  const [pos, setPos] = useState(null)

  const place = useCallback(() => {
    if (!anchorEl) return
    const rect = anchorEl.getBoundingClientRect()
    const margin = 32
    const maxHeight = Math.max(220, window.innerHeight - rect.bottom - margin)
    // Anchor below the toolbar button, right-aligned to it (the pane is narrow).
    const width = 320
    const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8))
    setPos({ top: rect.bottom + 6, left, width, maxHeight })
  }, [anchorEl])

  useLayoutEffect(() => {
    place()
    const handler = () => place()
    window.addEventListener('resize', handler)
    window.addEventListener('scroll', handler, true)
    return () => {
      window.removeEventListener('resize', handler)
      window.removeEventListener('scroll', handler, true)
    }
  }, [place])

  useEffect(() => {
    const onDown = (event) => {
      if (ref.current?.contains(event.target)) return
      if (anchorEl?.contains(event.target)) return
      if (event.target.closest?.('.ct-multiselect-list')) return
      onClose()
    }
    const onKey = (event) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onDown, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [anchorEl, onClose])

  if (!pos) return null

  const toggleAttr = (id) => {
    const set = new Set(filter.attrs)
    if (set.has(id)) set.delete(id)
    else set.add(id)
    onPatch({ attrs: [...set] })
  }

  return createPortal(
    <div
      className="cmt-filter-pop"
      ref={ref}
      style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
      role="dialog"
      aria-label="Filter comments"
    >
      <div className="cmt-filter-head">
        <span className="cmt-filter-title">Filter</span>
        <button
          type="button"
          className="cmt-filter-clear"
          onClick={onClear}
          disabled={!active}
        >
          Clear all
        </button>
      </div>

      <div className="cmt-filter-body">
        <div className="cmt-filter-mode">
          <span className="cmt-filter-label">Match</span>
          <div className="ct-toggle" role="group" aria-label="Match mode">
            <button
              type="button"
              className={'ct-toggle-btn' + (filter.matchMode === 'or' ? ' is-active' : '')}
              onClick={() => onPatch({ matchMode: 'or' })}
            >
              Any
            </button>
            <button
              type="button"
              className={'ct-toggle-btn' + (filter.matchMode === 'and' ? ' is-active' : '')}
              onClick={() => onPatch({ matchMode: 'and' })}
            >
              All
            </button>
          </div>
        </div>

        <div className="cmt-filter-facet">
          <span className="cmt-filter-label">Show</span>
          <div className="cmt-filter-attrs">
            {ATTR_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={'cmt-chip' + (filter.attrs.includes(opt.id) ? ' is-on' : '')}
                onClick={() => toggleAttr(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="cmt-filter-facet">
          <span className="cmt-filter-label">Author</span>
          <MultiSelect
            value={filter.authors}
            onChange={(next) => onPatch({ authors: next })}
            options={authorOptions}
            getOptionValue={(o) => o._id}
            getOptionLabel={(o) => o.label}
            placeholder={authorOptions.length ? 'Any author' : 'No authors yet'}
            disabled={!authorOptions.length}
          />
        </div>

        <div className="cmt-filter-facet">
          <span className="cmt-filter-label">Item</span>
          <MultiSelect
            value={filter.items}
            onChange={(next) => onPatch({ items: next })}
            options={itemOptions}
            getOptionValue={(o) => o._id}
            getOptionLabel={(o) => o.label}
            placeholder={itemOptions.length ? 'Any item' : 'No items yet'}
            disabled={!itemOptions.length}
          />
          <label className="cmt-filter-check">
            <input
              type="checkbox"
              checked={filter.includeDescendants}
              onChange={(e) => onPatch({ includeDescendants: e.target.checked })}
            />
            Include descendants of selected items
          </label>
        </div>

        <div className="cmt-filter-facet">
          <span className="cmt-filter-label">Topic</span>
          <MultiSelect
            value={filter.topics || []}
            onChange={(next) => onPatch({ topics: next })}
            options={topicOptions}
            getOptionValue={(t) => t._id}
            getOptionLabel={topicLabelFor(topicOptions)}
            placeholder={topicOptions.length ? 'Any topic' : 'No topics yet'}
            disabled={!topicOptions.length}
            clearQueryOnSelect={false}
          />
        </div>
      </div>
    </div>,
    document.body
  )
}

export default CommentsFilterPopover
